import { Select } from 'antd';
import type { SelectProps } from 'antd';
import { forwardRef } from 'react';
import {
  controlSizeForContext,
  CONTROL_CLASS,
} from '@/theme/controlSizing';
import type { ControlContext } from '@/theme/controlSizing';

export type { ControlContext };

/**
 * Props {@link SelectBox} — wrapper Antd Select dengan opinionated defaults.
 */
export interface SelectBoxProps<T = unknown>
  extends Omit<SelectProps<T>, 'size'> {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: ControlContext;
  /** Override paksa size Antd jika perlu. */
  size?: SelectProps['size'];
}

/**
 * BASE: Combobox / Dropdown selection.
 *
 * Sifat universal:
 * - Density mengikuti `context` (page=small, form/drawer=middle).
 * - Width default 100% (mengikuti container Form.Item / Col).
 * - Class `booku-control` → vertical alignment & padding seragam dengan
 *   komponen family lain (lihat `styles/controls.css`).
 *
 * Untuk varian searchable (autocomplete-like), pakai {@link ComboBox}
 * — secara semantik berbeda meski underlying-nya sama.
 *
 * Pemakaian:
 * ```tsx
 * <SelectBox
 *   value={kategori}
 *   onChange={setKategori}
 *   options={[
 *     { value: 'A', label: 'Aktiva' },
 *     { value: 'P', label: 'Passiva' },
 *   ]}
 * />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */
function SelectBoxInner<T = unknown>(
  {
    context = 'form',
    size,
    style,
    className,
    ...rest
  }: SelectBoxProps<T>,
  ref: React.Ref<unknown>
) {
  const resolvedSize = size ?? controlSizeForContext(context);
  return (
    <Select<T>
      ref={ref as React.Ref<never>}
      size={resolvedSize}
      className={`${CONTROL_CLASS}${className ? ` ${className}` : ''}`}
      style={{ width: '100%', ...style }}
      {...rest}
    />
  );
}

export const SelectBox = forwardRef(SelectBoxInner) as <T = unknown>(
  props: SelectBoxProps<T> & { ref?: React.Ref<unknown> }
) => React.ReactElement;
